import { rtkApi, GET_COURSES, GET_COURSE, GET_LESSONS } from '@/shared/api/rtkApi';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  CoursesResponse,
  CourseDetailResponse,
  IntakesResponse,
  IntakesParams,
  AssignableStudentsResponse,
  GroupParams,
  CreateTermRequest,
  CreateTermResponse,
  UpdateTermRequest,
  UpdateTermResponse,
  DeleteTermResponse,
  TermsResponse,
  TermsParams,
  AssignStudentsRequest,
  AssignStudentsResponse,
  CreateIntakeRequest,
  CreateIntakeResponse,
  UpdateIntakeRequest,
  UpdateIntakeResponse,
  CreateGroupRequest,
  CreateGroupResponse,
  UpdateGroupRequest,
  UpdateGroupResponse,
  DeleteGroupResponse,
  CreateCourseRequest,
  CreateCourseResponse,
  UpdateCourseRequest,
  UpdateCourseResponse,
  DeleteCourseResponse,
  HideCourseRequest,
  HideCourseResponse,
  DeleteIntakeResponse,
  HideIntakeRequest,
  HideIntakeResponse,
  TermModulesResponse,
  TermModulesParams,
  TermStudentsResponse,
  TermStudentsParams,
  AdvanceableStudentsResponse,
  AdvanceableStudentsParams,
  AttendanceResponse,
  AttendanceParams,
  CreateModuleRequest,
  CreateModuleResponse,
  UpdateModuleRequest,
  UpdateModuleResponse,
  DeleteModuleResponse,
  ModuleBaseInfoResponse,
  ModuleLessonsResponse,
  ModuleLessonsParams,
  GetLessonByModuleParams,
  GetLessonByModuleResponse,
  CreateLessonRequest,
  CreateLessonResponse,
  UpdateLessonRequest,
  UpdateLessonResponse,
  DeleteLessonResponse,
  CreateLessonStepRequest,
  CreateLessonStepResponse,
  UpdateContentStepRequest,
  UpdateContentStepResponse,
  DeleteContentStepResponse,
  ReorderLessonStepsRequest,
  ReorderLessonStepsResponse,
  UpdateContentStepScormRequest,
  UpdateContentStepScormResponse,
  DeleteContentStepScormResponse,
  ScormUploadFileRequest,
  StartContentStepScormUploadRequest,
  StartContentStepScormUploadResponse,
  ResumeContentStepScormUploadResponse,
  CompleteContentStepScormUploadResponse,
  CreateContentStepVideoTokenRequest,
  CreateContentStepVideoTokenResponse,
  DeleteContentStepVideoResponse,
  ScormPreviewResponse,
  CoursesStructureResponse,
  CoursesAllShortInfoResponse,
} from './types';

export const coursesApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getCourses: build.query<CoursesResponse, void>({
      query: () => ({
        url: '/admin/courses',
      }),
      providesTags: [GET_COURSES],
    }),
    getCoursesAllShortInfo: build.query<CoursesAllShortInfoResponse, void>({
      query: () => ({
        url: '/admin/courses/all-short-info',
      }),
      providesTags: [GET_COURSES],
    }),
    getCoursesStructure: build.query<CoursesStructureResponse, void>({
      query: () => ({
        url: '/admin/courses/structure',
      }),
      providesTags: [GET_COURSES],
    }),
    getCourseById: build.query<CourseDetailResponse, number>({
      query: (id) => ({
        url: `/admin/courses/${id}`,
      }),
      providesTags: [GET_COURSE],
    }),
    getIntakes: build.query<IntakesResponse, IntakesParams>({
      query: ({ courseId, ...params }) => ({
        url: `/admin/intakes/course/${courseId}`,
        params,
      }),
      providesTags: [GET_COURSE],
    }),
    getAssignableStudents: build.query<AssignableStudentsResponse, GroupParams>({
      query: ({ intakeId, groupNumber }) => ({
        url: `/admin/intakes/${intakeId}/groups/${groupNumber}/assignable`,
      }),
      providesTags: [GET_COURSE],
    }),
    createIntake: build.mutation<CreateIntakeResponse, CreateIntakeRequest>({
      query: (body) => ({
        url: '/admin/intakes',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    updateIntake: build.mutation<UpdateIntakeResponse, { id: number; body: UpdateIntakeRequest }>({
      query: ({ id, body }) => ({
        url: `/admin/intakes/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    createGroup: build.mutation<CreateGroupResponse, CreateGroupRequest>({
      query: (body) => ({
        url: '/admin/groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    updateGroup: build.mutation<
      UpdateGroupResponse,
      { intakeId: number; groupNumber: number; body: UpdateGroupRequest }
    >({
      query: ({ intakeId, groupNumber, body }) => ({
        url: `/admin/intakes/${intakeId}/groups/${groupNumber}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    deleteGroup: build.mutation<
      DeleteGroupResponse,
      { intakeId: number; groupNumber: number }
    >({
      query: ({ intakeId, groupNumber }) => ({
        url: `/admin/intakes/${intakeId}/groups/${groupNumber}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_COURSE],
    }),
    createTerm: build.mutation<CreateTermResponse, CreateTermRequest>({
      query: (body) => ({
        url: '/admin/terms',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    updateTerm: build.mutation<
      UpdateTermResponse,
      { intakeId: number; termNumber: number; body: UpdateTermRequest }
    >({
      query: ({ intakeId, termNumber, body }) => ({
        url: `/admin/intakes/${intakeId}/terms/${termNumber}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    getTerms: build.query<TermsResponse, TermsParams>({
      query: ({ intakeId }) => ({
        url: `/admin/intakes/${intakeId}/terms`,
      }),
      providesTags: [GET_COURSE],
    }),
    deleteTerm: build.mutation<DeleteTermResponse, number>({
      query: (id) => ({
        url: `/admin/terms/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_COURSE],
    }),
    assignStudents: build.mutation<
      AssignStudentsResponse,
      { intakeId: number; groupNumber: number; body: AssignStudentsRequest }
    >({
      query: ({ intakeId, groupNumber, body }) => ({
        url: `/admin/intakes/${intakeId}/groups/${groupNumber}/assign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    createCourse: build.mutation<CreateCourseResponse, CreateCourseRequest>({
      queryFn: async (body) => {
        const formData = new FormData();
        formData.append('title', body.title);
        formData.append('type', body.type);
        formData.append('level', body.level);
        formData.append('category', String(body.category));
        formData.append('price', String(body.price));
        formData.append('leadTeacher', String(body.leadTeacher));
        const descriptionValue =
          body.description && body.description.trim()
            ? body.description
            : 'Default course description';
        formData.append('description', descriptionValue);
        formData.append('isAI', String(body.isAI));
        formData.append('durationFullTime', String(body.durationFullTime));
        formData.append('durationPartTime', String(body.durationPartTime));
        if (body.certificateDescription?.trim()) {
          formData.append('certificateDescription', body.certificateDescription.trim());
        }

        if (body.saleStart) formData.append('saleStart', body.saleStart);
        if (body.saleEnd) formData.append('saleEnd', body.saleEnd);

        if (body.discountPrice !== undefined && body.discountPrice !== null) {
          formData.append('discountPrice', String(body.discountPrice));
          if (body.discountStart) {
            formData.append('discountStart', body.discountStart);
          } else if (body.saleStart) {
            formData.append('discountStart', body.saleStart);
          }
          if (body.discountEnd) {
            formData.append('discountEnd', body.discountEnd);
          } else if (body.saleEnd) {
            formData.append('discountEnd', body.saleEnd);
          }
        } else {
          if (body.discountStart) formData.append('discountStart', body.discountStart);
          if (body.discountEnd) formData.append('discountEnd', body.discountEnd);
        }

        if (body.degree) formData.append('degree', body.degree);
        if (body.pathway) formData.append('pathway', body.pathway);
        if (body.paymentLink?.trim()) formData.append('paymentLink', body.paymentLink.trim());
        if (body.seoTitle?.trim()) formData.append('seoTitle', body.seoTitle.trim());
        if (body.seoDescription?.trim()) formData.append('seoDescription', body.seoDescription.trim());

        const benefitsArray = body.benefits && body.benefits.length > 0 ? body.benefits : [];
        formData.append('benefits', JSON.stringify(benefitsArray));

        const targetAudienceArray =
          body.targetAudience && body.targetAudience.length > 0 ? body.targetAudience : [];
        formData.append('targetAudience', JSON.stringify(targetAudienceArray));

        const recommendedCoursesArray =
          body.recommendedCourses && body.recommendedCourses.length > 0
            ? body.recommendedCourses
            : [];
        formData.append('recommendedCourses', JSON.stringify(recommendedCoursesArray));

        if (body.imageFile) formData.append('imageFile', body.imageFile);

        if (body.previewFile) {
          formData.append('previewFile', body.previewFile);
        } else {
          const minimalMP4 = new Uint8Array([
            0x00, 0x00, 0x00, 0x20, 0x66, 0x74, 0x79, 0x70, 0x69, 0x73, 0x6f, 0x6d, 0x00, 0x00,
            0x02, 0x00, 0x69, 0x73, 0x6f, 0x6d, 0x69, 0x73, 0x6f, 0x32, 0x6d, 0x70, 0x34, 0x31,
          ]);
          const emptyVideoBlob = new Blob([minimalMP4], { type: 'video/mp4' });
          const emptyVideoFile = new File([emptyVideoBlob], 'empty-preview.mp4', {
            type: 'video/mp4',
          });
          formData.append('previewFile', emptyVideoFile);
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/courses`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_COURSES],
    }),
    updateCourse: build.mutation<UpdateCourseResponse, { id: number; body: UpdateCourseRequest }>({
      queryFn: async ({ id, body }) => {
        const formData = new FormData();
        formData.append('title', body.title);
        formData.append('type', body.type);
        formData.append('level', body.level);
        formData.append('category', String(body.category));
        formData.append('price', String(body.price));
        formData.append('leadTeacher', String(body.leadTeacher));
        const descriptionValue =
          body.description && body.description.trim()
            ? body.description
            : 'Default course description';
        formData.append('description', descriptionValue);
        formData.append('isAI', String(body.isAI));
        formData.append('durationFullTime', String(body.durationFullTime));
        formData.append('durationPartTime', String(body.durationPartTime));
        formData.append('certificateDescription', body.certificateDescription?.trim() || '');

        if (body.saleStart) formData.append('saleStart', body.saleStart);
        if (body.saleEnd) formData.append('saleEnd', body.saleEnd);

        if (body.discountPrice !== undefined && body.discountPrice !== null) {
          formData.append('discountPrice', String(body.discountPrice));
          if (body.discountStart) {
            formData.append('discountStart', body.discountStart);
          } else if (body.saleStart) {
            formData.append('discountStart', body.saleStart);
          }
          if (body.discountEnd) {
            formData.append('discountEnd', body.discountEnd);
          } else if (body.saleEnd) {
            formData.append('discountEnd', body.saleEnd);
          }
        } else {
          formData.append('discountPrice', '');
          if (body.discountStart) formData.append('discountStart', body.discountStart);
          if (body.discountEnd) formData.append('discountEnd', body.discountEnd);
        }

        if (body.degree) formData.append('degree', body.degree);
        if (body.pathway) formData.append('pathway', body.pathway);
        formData.append('paymentLink', body.paymentLink?.trim() || '');
        formData.append('seoTitle', body.seoTitle?.trim() || '');
        formData.append('seoDescription', body.seoDescription?.trim() || '');

        const benefitsArray = body.benefits && body.benefits.length > 0 ? body.benefits : [];
        formData.append('benefits', JSON.stringify(benefitsArray));

        const targetAudienceArray =
          body.targetAudience && body.targetAudience.length > 0 ? body.targetAudience : [];
        formData.append('targetAudience', JSON.stringify(targetAudienceArray));

        const recommendedCoursesArray =
          body.recommendedCourses && body.recommendedCourses.length > 0
            ? body.recommendedCourses
            : [];
        formData.append('recommendedCourses', JSON.stringify(recommendedCoursesArray));

        if (body.imageFile) formData.append('imageFile', body.imageFile);

        if (body.previewFile) {
          formData.append('previewFile', body.previewFile);
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/courses/${id}`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_COURSES, GET_COURSE],
    }),
    courseDuplication: build.mutation<CreateCourseResponse, { courseId: number }>({
      query: (body) => ({
        url: `/admin/courses/duplicate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSES, GET_COURSE],
    }),
    deleteCourse: build.mutation<DeleteCourseResponse, number>({
      query: (id) => ({
        url: `/admin/courses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_COURSES, GET_COURSE],
    }),
    hideCourse: build.mutation<HideCourseResponse, { id: number; body: HideCourseRequest }>({
      query: ({ id, body }) => ({
        url: `/admin/courses/${id}/visibility`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSES, GET_COURSE],
    }),
    deleteIntake: build.mutation<DeleteIntakeResponse, number>({
      query: (id) => ({
        url: `/admin/intakes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_COURSE],
    }),
    hideIntake: build.mutation<HideIntakeResponse, { id: number; body: HideIntakeRequest }>({
      query: ({ id, body }) => ({
        url: `/admin/intakes/${id}/visibility`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    getTermModules: build.query<TermModulesResponse, TermModulesParams>({
      query: ({ groupNumber, intakeTermId, ...params }) => ({
        url: `/admin/groups/${groupNumber}/terms/${intakeTermId}/modules`,
        params,
      }),
      providesTags: [GET_COURSE],
    }),
    getModuleBaseInfo: build.query<
      ModuleBaseInfoResponse,
      { intakeModuleId: number; groupId: number }
    >({
      query: ({ intakeModuleId, groupId }) => ({
        url: `/admin/modules/${intakeModuleId}/base-info`,
        params: { groupId },
      }),
      providesTags: [GET_COURSE],
    }),
    getTermStudents: build.query<TermStudentsResponse, TermStudentsParams>({
      query: ({ groupNumber, intakeTermId, ...params }) => ({
        url: `/admin/groups/${groupNumber}/terms/${intakeTermId}/students`,
        params,
      }),
      providesTags: [GET_COURSE],
    }),
    createModule: build.mutation<CreateModuleResponse, CreateModuleRequest>({
      query: (body) => ({
        url: '/admin/modules',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    updateModule: build.mutation<
      UpdateModuleResponse,
      { moduleId: number; body: UpdateModuleRequest }
    >({
      query: ({ moduleId, body }) => ({
        url: `/admin/modules/${moduleId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_COURSE],
    }),
    deleteModule: build.mutation<DeleteModuleResponse, number>({
      query: (moduleId) => ({
        url: `/admin/modules/${moduleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_COURSE, GET_LESSONS],
    }),
    getModuleLessons: build.query<ModuleLessonsResponse, ModuleLessonsParams>({
      query: ({ intakeModuleId, groupId }) => ({
        url: `/admin/modules/${intakeModuleId}/lessons`,
        params: { groupId },
      }),
      providesTags: [GET_LESSONS],
    }),
    getLessonByModule: build.query<GetLessonByModuleResponse, GetLessonByModuleParams>({
      query: ({ intakeModuleId, lessonId, groupId }) => ({
        url: `/admin/modules/${intakeModuleId}/lessons/${lessonId}`,
        params: { groupId },
      }),
      providesTags: [GET_LESSONS],
    }),
    getScormPreviewUrl: build.query<ScormPreviewResponse, number>({
      query: (packageId) => ({
        url: `/scorm/preview/${packageId}`,
      }),
    }),
    createLesson: build.mutation<
      CreateLessonResponse,
      { intakeModuleId: number; body: CreateLessonRequest }
    >({
      queryFn: async ({ intakeModuleId, body }) => {
        const formData = new FormData();
        formData.append('title', body.title);
        formData.append('description', body.description);
        formData.append('duration', String(body.duration));
        formData.append('groupId', String(body.groupId));
        formData.append('scheduledAt', body.scheduledAt);
        if (body.assignmentText !== undefined) {
          formData.append('assignmentText', body.assignmentText);
        }
        if (body.assignmentDescription !== undefined) {
          formData.append('assignmentDescription', body.assignmentDescription);
        }
        if (body.assignmentAttempts !== undefined) {
          formData.append('assignmentAttempts', String(body.assignmentAttempts));
        }
        if (body.format) {
          formData.append('format', body.format);
        }
        if (body.roomId !== undefined && body.roomId !== null) {
          formData.append('roomId', String(body.roomId));
        }
        if (body.conferenceLink) {
          formData.append('conferenceLink', body.conferenceLink);
        }

        if (body.isExam !== undefined) {
          formData.append('isExam', String(body.isExam));
        }
        if (body.isOnlyVideoLesson !== undefined) {
          formData.append('isOnlyVideoLesson', String(body.isOnlyVideoLesson));
        }
        if (body.endDate) {
          formData.append('endDate', body.endDate);
        }
        if (body.assignmentFiles && body.assignmentFiles.length > 0) {
          body.assignmentFiles.forEach((file) => {
            formData.append('assignmentFiles', file);
          });
        }
        if (body.lessonFiles && body.lessonFiles.length > 0) {
          body.lessonFiles.forEach((file) => {
            formData.append('lessonFiles', file);
          });
        }
        if (body.scormPackageFile) {
          formData.append('scormPackageFile', body.scormPackageFile);
        } else if (body.scormPackageUrl) {
          formData.append('scormPackageUrl', body.scormPackageUrl);
        }
        if (body.scormHasAssessment !== undefined) {
          formData.append('scormHasAssessment', String(body.scormHasAssessment));
        }
        if (body.scormPassingScore !== undefined) {
          formData.append('scormPassingScore', String(body.scormPassingScore));
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/modules/${intakeModuleId}/lessons`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_LESSONS],
    }),
    updateLesson: build.mutation<
      UpdateLessonResponse,
      { lessonId: number; body: UpdateLessonRequest }
    >({
      queryFn: async ({ lessonId, body }) => {
        const formData = new FormData();
        formData.append('title', body.title);
        formData.append('description', body.description);
        formData.append('duration', String(body.duration));
        if (body.removeAssignment === true) {
          formData.append('removeAssignment', 'true');
        } else {
          if (body.assignmentText !== undefined) {
            formData.append('assignmentText', body.assignmentText);
          }
          if (body.assignmentDescription !== undefined) {
            formData.append('assignmentDescription', body.assignmentDescription);
          }
        }
        if (body.format) {
          formData.append('format', body.format);
        }
        if (body.roomId !== undefined && body.roomId !== null) {
          formData.append('roomId', String(body.roomId));
        }
        if (body.conferenceLink) {
          formData.append('conferenceLink', body.conferenceLink);
        }

        if (body.scheduledAt) {
          formData.append('scheduledAt', body.scheduledAt);
        }
        if (body.groupId) {
          formData.append('groupId', String(body.groupId));
        }
        if (body.isExam !== undefined) {
          formData.append('isExam', String(body.isExam));
        }
        if (body.isOnlyVideoLesson !== undefined) {
          formData.append('isOnlyVideoLesson', String(body.isOnlyVideoLesson));
        }
        if (body.endDate) {
          formData.append('endDate', body.endDate);
        }
        if (body.removeAssignment !== true) {
          if (body.assignmentAttempts !== undefined) {
            formData.append('assignmentAttempts', String(body.assignmentAttempts));
          }
          if (body.assignmentFiles && body.assignmentFiles.length > 0) {
            body.assignmentFiles.forEach((file) => {
              formData.append('assignmentFiles', file);
            });
          }
        }
        if (body.lessonFiles && body.lessonFiles.length > 0) {
          body.lessonFiles.forEach((file) => {
            formData.append('lessonFiles', file);
          });
        }
        if (body.removeAssignment !== true && body.removeAssignmentAttachmentKeys?.length) {
          formData.append(
            'removeAssignmentAttachmentKeys',
            JSON.stringify(body.removeAssignmentAttachmentKeys),
          );
        }
        if (body.removeLessonAttachmentKeys?.length) {
          formData.append('removeLessonAttachmentKeys', JSON.stringify(body.removeLessonAttachmentKeys));
        }
        if (body.scormPackageFile) {
          formData.append('scormPackageFile', body.scormPackageFile);
        } else if (body.scormPackageUrl) {
          formData.append('scormPackageUrl', body.scormPackageUrl);
        }
        if (body.scormHasAssessment !== undefined) {
          formData.append('scormHasAssessment', String(body.scormHasAssessment));
        }
        if (body.scormPassingScore !== undefined) {
          formData.append('scormPassingScore', String(body.scormPassingScore));
        }
        if (body.removeScormPackage === true) {
          formData.append('removeScormPackage', 'true');
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/lessons/${lessonId}`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_LESSONS],
    }),
    deleteLesson: build.mutation<DeleteLessonResponse, number>({
      query: (lessonId) => ({
        url: `/admin/lessons/${lessonId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    createLessonStep: build.mutation<
      CreateLessonStepResponse,
      { lessonId: number; body: CreateLessonStepRequest }
    >({
      query: ({ lessonId, body }) => {
        const formData = new FormData();
        if (body.type) formData.append('type', body.type);
        if (body.title !== undefined) formData.append('title', body.title);
        if (body.description !== undefined) formData.append('description', body.description);
        if (body.content !== undefined) formData.append('content', body.content);
        if (body.order !== undefined) formData.append('order', String(body.order));
        body.attachments?.forEach((file) => {
          formData.append('attachments', file);
        });

        return {
          url: `/admin/lessons/${lessonId}/steps`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [GET_LESSONS],
    }),
    createResubmissionStep: build.mutation<
      CreateLessonStepResponse,
      { resubmissionId: number; body: CreateLessonStepRequest }
    >({
      query: ({ resubmissionId, body }) => {
        const formData = new FormData();
        if (body.type) formData.append('type', body.type);
        if (body.title !== undefined) formData.append('title', body.title);
        if (body.description !== undefined) formData.append('description', body.description);
        if (body.content !== undefined) formData.append('content', body.content);
        if (body.order !== undefined) formData.append('order', String(body.order));
        body.attachments?.forEach((file) => {
          formData.append('attachments', file);
        });

        return {
          url: `/admin/resubmissions/${resubmissionId}/steps`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [GET_LESSONS],
    }),
    updateContentStep: build.mutation<
      UpdateContentStepResponse,
      { stepId: number; body: UpdateContentStepRequest }
    >({
      query: ({ stepId, body }) => {
        const formData = new FormData();
        if (body.type) formData.append('type', body.type);
        if (body.title !== undefined) formData.append('title', body.title);
        if (body.description !== undefined) formData.append('description', body.description);
        if (body.content !== undefined) formData.append('content', body.content);
        body.attachments?.forEach((file) => {
          formData.append('attachments', file);
        });
        if (body.removeAttachmentKeys?.length) {
          formData.append('removeAttachmentKeys', JSON.stringify(body.removeAttachmentKeys));
        }

        return {
          url: `/admin/content-steps/${stepId}`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: [GET_LESSONS],
    }),
    deleteContentStep: build.mutation<DeleteContentStepResponse, number>({
      query: (stepId) => ({
        url: `/admin/content-steps/${stepId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    reorderLessonSteps: build.mutation<
      ReorderLessonStepsResponse,
      { lessonId: number; body: ReorderLessonStepsRequest }
    >({
      query: ({ lessonId, body }) => ({
        url: `/admin/lessons/${lessonId}/steps/order`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    reorderResubmissionSteps: build.mutation<
      ReorderLessonStepsResponse,
      { resubmissionId: number; body: ReorderLessonStepsRequest }
    >({
      query: ({ resubmissionId, body }) => ({
        url: `/admin/resubmissions/${resubmissionId}/steps/order`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    updateContentStepScorm: build.mutation<
      UpdateContentStepScormResponse,
      { stepId: number; body: UpdateContentStepScormRequest }
    >({
      query: ({ stepId, body }) => ({
        url: `/admin/content-steps/${stepId}/scorm`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    deleteContentStepScorm: build.mutation<DeleteContentStepScormResponse, number>({
      query: (stepId) => ({
        url: `/admin/content-steps/${stepId}/scorm`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    // Opens a direct upload: removes the step's current package and creates one in 'uploading'
    startContentStepScormUpload: build.mutation<
      StartContentStepScormUploadResponse,
      { stepId: number; body: StartContentStepScormUploadRequest }
    >({
      query: ({ stepId, body }) => ({
        url: `/admin/content-steps/${stepId}/scorm-upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    // The same file: the parts already accepted and fresh links for the missing ones
    resumeContentStepScormUpload: build.mutation<
      ResumeContentStepScormUploadResponse,
      { stepId: number; body: ScormUploadFileRequest }
    >({
      query: ({ stepId, body }) => ({
        url: `/admin/content-steps/${stepId}/scorm-upload/resume`,
        method: 'POST',
        body,
      }),
    }),
    // Assembles the uploaded parts and queues the package for processing; 400 lists the parts
    // the storage has not received (missingPartNumbers)
    completeContentStepScormUpload: build.mutation<CompleteContentStepScormUploadResponse, number>({
      query: (stepId) => ({
        url: `/admin/content-steps/${stepId}/scorm-upload/complete`,
        method: 'POST',
      }),
    }),
    createContentStepVideoToken: build.mutation<
      CreateContentStepVideoTokenResponse,
      { stepId: number; body: CreateContentStepVideoTokenRequest }
    >({
      query: ({ stepId, body }) => ({
        url: `/admin/content-steps/${stepId}/upload-video-token`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    deleteContentStepVideo: build.mutation<DeleteContentStepVideoResponse, number>({
      query: (stepId) => ({
        url: `/admin/content-steps/${stepId}/video`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_LESSONS],
    }),
    getAdvanceableStudents: build.query<AdvanceableStudentsResponse, AdvanceableStudentsParams>({
      query: ({ intakeId, group, fromTermNumber }) => ({
        url: `/admin/intakes/${intakeId}/enrollments/advanceable`,
        params: {
          ...(group !== undefined && { group }),
          fromTermNumber,
        },
      }),
      providesTags: [GET_COURSE],
    }),
    getAttendance: build.query<AttendanceResponse, AttendanceParams>({
      query: ({ intakeModuleId, groupNumber }) => ({
        url: `/admin/modules/${intakeModuleId}/group/${groupNumber}/attendance`,
      }),
      providesTags: [GET_COURSE],
    }),
  }),
});

export const {
  useGetCoursesQuery,
  useGetCoursesAllShortInfoQuery,
  useGetCoursesStructureQuery,
  useGetCourseByIdQuery,
  useGetIntakesQuery,
  useGetAssignableStudentsQuery,
  useLazyGetAssignableStudentsQuery,
  useCreateIntakeMutation,
  useUpdateIntakeMutation,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useCreateTermMutation,
  useUpdateTermMutation,
  useDeleteTermMutation,
  useGetTermsQuery,
  useAssignStudentsMutation,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useCourseDuplicationMutation,
  useHideCourseMutation,
  useDeleteIntakeMutation,
  useHideIntakeMutation,
  useGetTermModulesQuery,
  useGetTermStudentsQuery,
  useCreateModuleMutation,
  useUpdateModuleMutation,
  useDeleteModuleMutation,
  useGetModuleBaseInfoQuery,
  useGetModuleLessonsQuery,
  useGetLessonByModuleQuery,
  useGetScormPreviewUrlQuery,
  useLazyGetScormPreviewUrlQuery,
  useCreateLessonMutation,
  useUpdateLessonMutation,
  useDeleteLessonMutation,
  useCreateLessonStepMutation,
  useCreateResubmissionStepMutation,
  useUpdateContentStepMutation,
  useDeleteContentStepMutation,
  useReorderLessonStepsMutation,
  useReorderResubmissionStepsMutation,
  useUpdateContentStepScormMutation,
  useDeleteContentStepScormMutation,
  useStartContentStepScormUploadMutation,
  useResumeContentStepScormUploadMutation,
  useCompleteContentStepScormUploadMutation,
  useCreateContentStepVideoTokenMutation,
  useDeleteContentStepVideoMutation,
  useGetAdvanceableStudentsQuery,
  useGetAttendanceQuery,
} = coursesApi;
